// The heatmap toolbar — a single, scannable control strip split into two zones:
//
//   shape ─────────────────────────────┐        ┌───────────── filter
//   View [Definitions|Available]        │        │  Location ▾  Doctors ▾
//   Group [Doctor|Tenant]               │        │  Reset
//   Week  ‹ 5 Jun → 11 Jun ›  Today     │        │
//
// The left zone shapes the data (which dataset, how it's grouped, which week).
// The right zone narrows it (location/doctor facets + reset). Each facet has its
// own in-popover search, so a separate global search box is no longer needed.
//
// Tenant and doctor selection used to be long wrapping rows of chips — one pill
// per entity — which fell apart with more than a handful of doctors. They're now
// searchable faceted multi-selects (popover + command palette): a compact
// trigger that summarises the active selection, opening to a filterable,
// keyboard-navigable checklist with Select all / Clear. This scales to hundreds
// of entities without touching the toolbar's footprint.

import { useMemo, useState } from "react";
import {
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FilterX,
  Stethoscope,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { FacetFilter, SegmentedToggle } from "../facet-filter";
import { hashHue } from "./heatmap-utils";

const VIEW_MODES = [
  {
    id: "definitions",
    label: "Definitions",
    icon: CalendarRange,
    title: "All slot definitions — the recurring weekly template",
  },
  {
    id: "available",
    label: "Available",
    icon: CalendarCheck,
    title: "Live availability — only free (unbooked) slots this week",
  },
];

const GROUP_BY_MODES = [
  {
    id: "doctor",
    label: "Doctor",
    icon: Stethoscope,
    title: "Group slots by doctor (location lanes nested inside)",
  },
  {
    id: "tenant",
    label: "Clinic",
    icon: Building2,
    title: "Group slots by location (doctor lanes nested inside)",
  },
];

const tenantHueStyle = (id) => ({ background: `oklch(0.62 0.16 ${hashHue(id)})` });

// A small muted caption that prefixes a control group (View / Group / Week).
function FieldLabel({ children }) {
  return (
    <span className="select-none text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
      {children}
    </span>
  );
}

// A hairline vertical divider between logical control groups.
function VDivider() {
  return <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-border" />;
}

// Compact h-8 control used for week navigation (prev / next / today).
function NavButton({ onClick, ariaLabel, title, className, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      className={cn(
        "inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-2 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
        className,
      )}
    >
      {children}
    </button>
  );
}

// The week navigator: prev / next steppers flanking a clickable range label.
// Clicking the label opens a month calendar — picking any day jumps the heatmap
// to the week that contains it. The week currently in view is highlighted as a
// connected band. The calendar inherits the app's first-day-of-week setting, so
// the band always lands on a single row and matches the heatmap's columns.
// Exported: the Sessions roster toolbar reuses it unchanged.
export function WeekPicker({ rangeLabel, weekStartDate, onPickDate, onPrevWeek, onNextWeek }) {
  const [open, setOpen] = useState(false);

  // The seven Date objects of the week currently on screen (in the configured
  // first-day order), used to paint the highlight band in the calendar.
  const weekDays = useMemo(() => {
    if (!weekStartDate) return [];
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStartDate);
      d.setDate(d.getDate() + i);
      d.setHours(0, 0, 0, 0);
      return d;
    });
  }, [weekStartDate]);

  return (
    <div className="inline-flex h-8 items-center overflow-hidden rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={onPrevWeek}
        aria-label="Previous week"
        title="Previous week"
        className="inline-flex h-full items-center px-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <ChevronLeft className="size-4" />
      </button>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="Pick a day to jump to its week"
            aria-label="Pick a day to jump to its week"
            className="inline-flex h-full items-center gap-1.5 border-x border-border px-2.5 text-foreground/90 transition-colors hover:bg-muted"
          >
            <CalendarDays className="size-3.5 text-muted-foreground" />
            <span className="font-mono text-[12px] tabular-nums">{rangeLabel}</span>
            <ChevronDown className="size-3.5 text-muted-foreground/70" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-auto p-0">
          <Calendar
            mode="single"
            defaultMonth={weekStartDate || undefined}
            onSelect={(date) => {
              if (!date) return;
              onPickDate?.(date);
              setOpen(false);
            }}
            modifiers={{
              weekStart: weekDays[0] ? [weekDays[0]] : [],
              weekMid: weekDays.slice(1, 6),
              weekEnd: weekDays[6] ? [weekDays[6]] : [],
            }}
            modifiersClassNames={{
              weekStart: "bg-accent text-accent-foreground rounded-l-md rounded-r-none",
              weekMid: "bg-accent text-accent-foreground rounded-none",
              weekEnd: "bg-accent text-accent-foreground rounded-r-md rounded-l-none",
            }}
          />
          <div className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
            Pick any day — the heatmap jumps to that week.
          </div>
        </PopoverContent>
      </Popover>

      <button
        type="button"
        onClick={onNextWeek}
        aria-label="Next week"
        title="Next week"
        className="inline-flex h-full items-center px-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
  );
}

export function Toolbar({
  viewMode,
  onViewModeChange,
  groupBy,
  onGroupByChange,
  rangeLabel,
  weekStartDate,
  onPickDate,
  isCurrentWeek,
  onPrevWeek,
  onNextWeek,
  onToday,
  tenants = [],
  selectedTenantIds = [],
  onToggleTenant,
  onSelectAllTenants,
  onClearTenants,
  doctors = [],
  selectedDoctorIds = [],
  onToggleDoctor,
  onSelectAllDoctors,
  onClearDoctors,
}) {
  // A filter is "active" only when its options have loaded and the selection is
  // a strict subset — that's what the Reset affordance and accenting key off.
  const tenantsNarrowed =
    tenants.length > 0 && selectedTenantIds.length !== tenants.length;
  const doctorsNarrowed =
    doctors.length > 0 && selectedDoctorIds.length !== doctors.length;
  const hasActiveFilters = tenantsNarrowed || doctorsNarrowed;

  const handleReset = () => {
    onSelectAllTenants?.();
    onSelectAllDoctors?.();
  };

  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 border-b border-border bg-muted/30 px-5 py-2.5">
      {/* ── Shape zone ── */}
      <div className="flex items-center gap-1.5">
        <FieldLabel>View</FieldLabel>
        <SegmentedToggle
          value={viewMode}
          segments={VIEW_MODES}
          onChange={onViewModeChange}
          ariaLabel="View mode"
        />
      </div>

      <VDivider />

      <div className="flex items-center gap-1.5">
        <FieldLabel>Group</FieldLabel>
        <SegmentedToggle
          value={groupBy}
          segments={GROUP_BY_MODES}
          onChange={onGroupByChange}
          ariaLabel="Group by"
        />
      </div>

      <VDivider />

      <div className="flex items-center gap-1.5">
        <FieldLabel>Week</FieldLabel>
        <WeekPicker
          rangeLabel={rangeLabel}
          weekStartDate={weekStartDate}
          onPickDate={onPickDate}
          onPrevWeek={onPrevWeek}
          onNextWeek={onNextWeek}
        />
        {!isCurrentWeek ? (
          <NavButton
            onClick={onToday}
            ariaLabel="Jump to the current week"
            title="Jump to the current week"
            className="text-foreground/80"
          >
            <CalendarClock className="size-3.5" />
            Today
          </NavButton>
        ) : null}
      </div>

      {/* ── Filter zone (pushed right) ── */}
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        <FacetFilter
          icon={Building2}
          label="Location"
          options={tenants}
          selectedIds={selectedTenantIds}
          onToggle={onToggleTenant}
          onSelectAll={onSelectAllTenants}
          onClear={onClearTenants}
          dotColor={(opt) => tenantHueStyle(opt.id).background}
          searchPlaceholder="Search locations…"
          align="end"
        />
        <FacetFilter
          icon={Stethoscope}
          label="Doctors"
          options={doctors}
          selectedIds={selectedDoctorIds}
          onToggle={onToggleDoctor}
          onSelectAll={onSelectAllDoctors}
          onClear={onClearDoctors}
          searchPlaceholder="Search doctors…"
          align="end"
        />

        {hasActiveFilters ? (
          <button
            type="button"
            onClick={handleReset}
            title="Clear all filters"
            className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <FilterX className="size-3.5" />
            Reset
          </button>
        ) : null}
      </div>
    </div>
  );
}
